import { revokeObjectURLOptions } from "@/shared/lib/utilities/object-url";
import { backendApi } from "@/services/backend/api";
import {
  DocumentBlob,
  DocumentBlobDeleteRequest,
  DocumentBlobList,
  DocumentBlobListRequest,
  DocumentBlobLoadRequest,
} from "@bitory-gmbh/bsm";

const documentBlobApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["DocumentBlob"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      documentBlobStore: build.mutation<DocumentBlob, StoreRequest>({
        query: (request) => {
          const body = new FormData();
          body.append("subdomain", request.subdomain);
          body.append("documentKind", request.documentKind);
          body.append("documentId", request.documentId);
          if (request.version) {
            body.append("version", request.version);
          }
          body.append("kind", request.kind);
          body.append("filename", request.filename);
          if (request.mediaType) {
            body.append("media-type", request.mediaType);
          }
          body.append("data", request.data, request.data.name);
          return {
            url: `/filestorage/document-blob`,
            method: "PUT",
            body,
            formData: true,
          };
        },
        invalidatesTags: ["DocumentBlob"],
      }),
      documentBlobLoad: build.query<
        { objectURL: string; contentType: string },
        DocumentBlobLoadRequest
      >({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: `/filestorage/document-blob/${request.subdomain}/${request.documentKind}/${request.documentId}/${request.filename}`,
          method: "GET",
          // otherwise, the backend will try to return json
          headers: { Accept: "application/octet-stream" },
          params: {
            kind: request.kind,
            version: request.version,
          },
          responseHandler: async (response) => {
            if (response.status !== 200) {
              // pass through error responses
              return response.json();
            }
            return {
              objectURL: window.URL.createObjectURL(await response.blob()),
              contentType: response.headers.get("content-type"),
            };
          },
          // Keep the file out of the browser's HTTP cache.
          cache: "no-store",
        }),
        providesTags: ["DocumentBlob"],
      }),
      documentBlobList: build.query<DocumentBlobList, DocumentBlobListRequest>({
        query: (request) => ({
          url: `/filestorage/document-blob`,
          method: "GET",
          params: request,
        }),
        providesTags: ["DocumentBlob"],
      }),
      documentBlobDelete: build.mutation<void, DocumentBlobDeleteRequest>({
        query: (request) => ({
          url: `/filestorage/document-blob`,
          method: "DELETE",
          params: request,
        }),
        invalidatesTags: ["DocumentBlob"],
      }),
    }),
  });

export const {
  useDocumentBlobStoreMutation,
  useDocumentBlobLoadQuery,
  useDocumentBlobListQuery,
  useDocumentBlobDeleteMutation,
} = documentBlobApi;

export type StoreRequest = DocumentBlob & {
  data: File;
};
